import type { MapNode, NodeId, NodeMap } from '../types';
import type { GraphIndex } from '../graph';
import { LAYOUT, type LayoutConstants } from './constants';

/**
 * The tree layout — ONE pure function replacing both `autoLayout()` and
 * `computeDisplayLayout()`.
 *
 * Those two are duplicated traversals: same `hOf`/`cy`/`kidsOf` ordering, same `shiftSide`,
 * same branch centring. They differ in exactly two things — which height they place with,
 * and where they write the answer — so the duplication collapses into a height parameter and
 * two thin adapters at the bottom of this file.
 *
 * THE IMPORTANT PART IS WHAT IS ABSENT: no DOM. The vanilla version calls `render()` first
 * and then measures each node with `offsetHeight`, which is why layout could never be tested
 * and why it costs a full rebuild every time. Here heights arrive as a function, fed in React
 * by a shared ResizeObserver. That is the whole point of the exercise.
 */

export interface LayoutPosition {
  x: number;
  y: number;
  side: number;
  depth: number;
}

export interface LayoutInput {
  nodes: NodeMap;
  graph: GraphIndex;
  rootId: NodeId;
  /** Measured height of a node's BOX. Ordering always uses this, never the total. */
  heightOf: (id: NodeId) => number;
  /**
   * Extra height below the box — expanded documentation rows. Omitted for the canonical
   * layout; supplied by the display pass. This is the entire difference between the two.
   */
  extraHeightOf?: (id: NodeId) => number;
  isPermHidden?: (id: NodeId) => boolean;
  /** Ids treated as expanded to reveal a filter hit, without touching their real flags. */
  forceOpen?: ReadonlySet<NodeId>;
  constants?: Partial<LayoutConstants>;
}

export interface LayoutCoreResult {
  /** Positions for the nodes that were actually PLACED. Excludes the root. */
  placed: Map<NodeId, LayoutPosition>;
  /**
   * The side-centring offset applied to each node UNDER a top-level branch that was not
   * placed — collapsed away, or permission-hidden below a visible ancestor.
   *
   * This exists because the two originals disagree here, and the disagreement is real. The
   * canonical pass shifts every descendant, placed or not:
   *
   *     [topId,...descendants(topId)].forEach(i=>{nodes[i].y+=off;});
   *
   * while the display pass guards with `if(displayY[i]!==undefined)`. Both are correct for
   * what they do — canonical y is persisted, so a collapsed branch must stay in step with
   * its parent or it scatters when expanded; display y is ephemeral and simply absent for
   * anything not on screen. Recording the offset separately lets one core serve both.
   */
  unplacedShift: Map<NodeId, number>;
  /** Total height used, before the anchor shift. */
  height: number;
  /** Where the root sits in the layout's own coordinate space, before the shift. */
  rootLayoutY: number;
}

interface Span {
  top: number;
  bottom: number;
}

/**
 * Lay the tree out in its own coordinate space, root at x=0.
 *
 * Note what `cy` reads: a node's CURRENT y, so a node dragged above or below its siblings
 * keeps that vertical order when the tree is realigned. In the vanilla version that read is
 * interleaved with writes to the same field, which happens to be safe because every parent's
 * children are ordered before any of them move. Here nothing is mutated at all, so the
 * question does not arise.
 */
function layoutCore(input: LayoutInput): LayoutCoreResult {
  const C = { ...LAYOUT, ...input.constants };
  const { nodes, graph, rootId } = input;
  const permHidden = input.isPermHidden ?? (() => false);
  const forceOpen = input.forceOpen;

  const placed = new Map<NodeId, LayoutPosition>();
  const unplacedShift = new Map<NodeId, number>();
  const sizeOf = (id: NodeId) => input.heightOf(id) + (input.extraHeightOf?.(id) ?? 0);
  const cy = (id: NodeId) => (nodes[id]?.y || 0) + input.heightOf(id) / 2;

  /** Children to lay out, in vertical order. `filter` copies, so the index is never sorted. */
  const kidsOf = (id: NodeId): NodeId[] =>
    graph
      .childrenOf(id)
      .filter((c) => !permHidden(c))
      .sort((a, b) => cy(a) - cy(b));

  function placeSubtree(id: NodeId, depth: number, dir: number, cursor: { y: number }): Span {
    const n: MapNode | undefined = nodes[id];
    const x = dir * depth * C.COLW; // same depth => same column, per side
    // A force-opened branch is laid out as if expanded, so revealed children land properly
    // instead of being left at stale positions.
    const collapsed = !!n?.collapsed && !forceOpen?.has(id);
    const kids = collapsed ? [] : kidsOf(id);

    if (!kids.length) {
      const h = sizeOf(id);
      const y = cursor.y;
      placed.set(id, { x, y, side: dir, depth });
      cursor.y += h + C.VGAP;
      return { top: y, bottom: y + h };
    }

    let first: number | null = null;
    let last = 0;
    for (const cid of kids) {
      const span = placeSubtree(cid, depth + 1, dir, cursor);
      if (first === null) first = span.top;
      last = span.bottom;
    }

    const h = sizeOf(id);
    const y = (first! + last) / 2 - h / 2; // centre the parent on its children
    placed.set(id, { x, y, side: dir, depth });
    return { top: Math.min(y, first!), bottom: Math.max(y + h, last) };
  }

  // Top-level branches, split by the side they are already on.
  const tops = graph.childrenOf(rootId).filter((id) => !permHidden(id));
  const rightTops = tops.filter((id) => (nodes[id]?.side || 1) >= 0).sort((a, b) => cy(a) - cy(b));
  const leftTops = tops.filter((id) => (nodes[id]?.side || 1) < 0).sort((a, b) => cy(a) - cy(b));

  const layoutSideTops = (list: NodeId[], dir: number): number => {
    const cursor = { y: 0 };
    for (const id of list) {
      placeSubtree(id, 1, dir, cursor);
      cursor.y += C.BRANCHGAP;
    }
    // Includes the trailing BRANCHGAP, and each leaf's trailing VGAP. Preserved deliberately:
    // it feeds the centring below, so "tidying" it moves every node on the shorter side.
    return cursor.y;
  };

  const rH = layoutSideTops(rightTops, 1);
  const lH = layoutSideTops(leftTops, -1);
  const height = Math.max(rH, lH, C.ROOTH);

  // Centre each side vertically within the taller of the two.
  const shiftSide = (list: NodeId[], used: number) => {
    const off = (height - used) / 2;
    if (!off) return;
    for (const topId of list) {
      for (const i of [topId, ...graph.descendants(topId)]) {
        const p = placed.get(i);
        if (p) p.y += off;
        // Not placed — collapsed away, or hidden below a visible ancestor. The canonical
        // caller still needs this offset; the display caller ignores it. See the note on
        // LayoutCoreResult.unplacedShift.
        else unplacedShift.set(i, (unplacedShift.get(i) ?? 0) + off);
      }
    }
  };
  shiftSide(rightTops, rH);
  shiftSide(leftTops, lH);

  return { placed, unplacedShift, height, rootLayoutY: height / 2 - input.heightOf(rootId) / 2 };
}

/**
 * The CANONICAL layout — the replacement for `autoLayout()`.
 *
 * Writes x, y, side and depth for every node, anchored so the root ends up exactly where it
 * was. That anchoring is what keeps the camera still: realigning must not make the view jump.
 *
 * Nodes that were never placed — collapsed away, or permission-hidden — still receive the
 * anchor shift, exactly as the original does (`for(const id in nodes){nodes[id].x+=dx;...}`).
 * That looks like an oddity and is load-bearing: those coordinates are persisted, so leaving
 * them un-shifted would scatter a branch the moment it was expanded.
 */
export function canonicalLayout(
  input: LayoutInput & { anchor?: { x: number; y: number } },
): Record<NodeId, LayoutPosition> {
  const root = input.nodes[input.rootId];
  if (!root) return {};

  const { placed, unplacedShift, rootLayoutY } = layoutCore(input);

  // Default anchor: wherever the root already is.
  const anchor = input.anchor ?? { x: root.x, y: root.y };
  const dx = anchor.x - 0;
  const dy = anchor.y - rootLayoutY;

  const out: Record<NodeId, LayoutPosition> = {};
  for (const id in input.nodes) {
    const p = placed.get(id);
    if (p) {
      out[id] = { x: p.x + dx, y: p.y + dy, side: p.side, depth: p.depth };
    } else if (id === input.rootId) {
      out[id] = { x: dx, y: rootLayoutY + dy, side: root.side, depth: root.depth };
    } else {
      // Keeps its old coordinates, plus the side-centring offset if it sits under a branch
      // that moved, plus the anchor shift that every node receives.
      const n = input.nodes[id];
      out[id] = {
        x: n.x + dx,
        y: n.y + (unplacedShift.get(id) ?? 0) + dy,
        side: n.side,
        depth: n.depth,
      };
    }
  }
  return out;
}

/**
 * The DISPLAY layout — the replacement for `computeDisplayLayout()`.
 *
 * Ephemeral and render-only: y values for placed nodes, keyed for `posY()` to prefer while
 * the documentation rows are expanded. It writes nothing canonical and triggers no save, so
 * zooming back out — or reloading — restores the exact stored compact layout and a purely
 * visual gesture never fires a PUT.
 *
 * Anchored on the root's canonical y rather than a prior screen position, because in
 * display-only space there is no such thing. The root is excluded: it never expands, so its
 * displayed y is its canonical one and an entry would be redundant.
 */
export function displayLayout(input: LayoutInput): Record<NodeId, number> {
  const root = input.nodes[input.rootId];
  if (!root) return {};

  const { placed, rootLayoutY } = layoutCore(input);
  const dy = root.y - rootLayoutY;

  const out: Record<NodeId, number> = {};
  for (const [id, p] of placed) out[id] = p.y + dy;
  return out;
}
